/**
 * Notification History Store — tracks recent app notifications for in-app review.
 *
 * Persists the last 50 notifications to AsyncStorage so users can see
 * dismissed notifications they may have missed.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';

export interface NotificationRecord {
  id: string;
  title: string;
  body: string;
  timestamp: string;
  type: 'reminder' | 'message' | 'system';
  read: boolean;
}

const MAX_HISTORY = 50;

interface NotificationHistoryState {
  notifications: NotificationRecord[];
  addNotification: (n: Omit<NotificationRecord, 'id' | 'timestamp' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearHistory: () => void;
  unreadCount: () => number;
}

export const useNotificationHistory = create<NotificationHistoryState>()(
  persist(
    (set, get) => ({
      notifications: [],

      addNotification: (n) => {
        const record: NotificationRecord = {
          ...n,
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          timestamp: new Date().toISOString(),
          read: false,
        };
        set(state => ({
          notifications: [record, ...state.notifications].slice(0, MAX_HISTORY),
        }));
      },

      markRead: (id) => {
        set(state => ({
          notifications: state.notifications.map(n =>
            n.id === id ? { ...n, read: true } : n
          ),
        }));
      },

      markAllRead: () => {
        set(state => ({
          notifications: state.notifications.map(n => ({ ...n, read: true })),
        }));
      },

      clearHistory: () => set({ notifications: [] }),

      unreadCount: () => get().notifications.filter(n => !n.read).length,
    }),
    {
      name: 'notification-history',
      storage: createJSONStorage(() => AsyncStorage),
    }
  )
);
